import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Volume2, BookOpen, Target, Award } from 'lucide-react';
import { whWordsData } from '@/data/whWordsData';
import WhWordCard from './WhWordCard';
import TaskBasedExercises from './TaskBasedExercises';
import EndQuiz from './EndQuiz';
import VisualFlashcards from './VisualFlashcards';

const LessonHub: React.FC = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'english' | 'french'>('english');
  const [activeTab, setActiveTab] = useState('overview');

  const toggleLanguage = () => {
    setCurrentLanguage(prev => prev === 'english' ? 'french' : 'english');
  };

  const speakText = (text: string, lang: 'en-US' | 'fr-FR' = 'en-US') => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">
            {currentLanguage === 'english' ? 'Wh-Questions in English' : 'Les Questions en Anglais'}
          </h1>
          <p className="text-lg text-muted-foreground mb-4">
            {currentLanguage === 'english' 
              ? 'A Task-Based Lesson for A2 French Students' 
              : 'Une Leçon Basée sur les Tâches pour les Étudiants Français A2'}
          </p>
          
          {/* Language Toggle */}
          <div className="flex justify-center gap-2 mb-6">
            <Button
              onClick={() => setCurrentLanguage('english')}
              className={currentLanguage === 'english' ? 'language-active' : 'language-inactive'}
              variant={currentLanguage === 'english' ? 'default' : 'outline'}
            >
              English
            </Button>
            <Button
              onClick={() => setCurrentLanguage('french')}
              className={currentLanguage === 'french' ? 'language-active' : 'language-inactive'}
              variant={currentLanguage === 'french' ? 'default' : 'outline'}
            >
              Français
            </Button>
          </div>

          {/* Lesson Duration Badge */}
          <Badge variant="secondary" className="text-sm px-4 py-2">
            <BookOpen className="w-4 h-4 mr-2" />
            {currentLanguage === 'english' ? '45 Minutes Lesson' : 'Leçon de 45 Minutes'}
          </Badge>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-8">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {currentLanguage === 'english' ? 'Overview' : 'Aperçu'}
            </TabsTrigger>
            <TabsTrigger value="words" className="flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              {currentLanguage === 'english' ? 'Wh-Words' : 'Mots-Qu'}
            </TabsTrigger>
            <TabsTrigger value="flashcards" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              {currentLanguage === 'english' ? 'Flashcards' : 'Cartes'}
            </TabsTrigger>
            <TabsTrigger value="exercises" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              {currentLanguage === 'english' ? 'Exercises' : 'Exercices'}
            </TabsTrigger>
            <TabsTrigger value="quiz" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              {currentLanguage === 'english' ? 'Quiz' : 'Quiz'}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-primary" />
                  {currentLanguage === 'english' ? 'Lesson Overview' : 'Aperçu de la Leçon'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-primary">
                      {currentLanguage === 'english' ? 'Learning Objectives' : 'Objectifs d\'Apprentissage'}
                    </h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                        {currentLanguage === 'english' 
                          ? 'Master the 7 main Wh-question words in English'
                          : 'Maîtriser les 7 principaux mots-questions en anglais'}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                        {currentLanguage === 'english' 
                          ? 'Understand their French equivalents and usage'
                          : 'Comprendre leurs équivalents français et leur usage'}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                        {currentLanguage === 'english' 
                          ? 'Practice with daily routine and personal information contexts'
                          : 'Pratiquer avec des contextes de routine quotidienne et d\'informations personnelles'}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                        {currentLanguage === 'english' 
                          ? 'Learn correct pronunciation with audio support'
                          : 'Apprendre la prononciation correcte avec support audio'}
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-primary">
                      {currentLanguage === 'english' ? 'The 7 Wh-Words' : 'Les 7 Mots-Qu'}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {whWordsData.map((word, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                          <div>
                            <span className="font-semibold text-primary">{word.word}</span>
                            <span className="text-sm text-muted-foreground ml-2">
                              {currentLanguage === 'english' ? word.word : word.french}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(word.word)}
                            className="audio-button"
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wh-Words Tab */}
          <TabsContent value="words">
            <div className="grid gap-6">
              {whWordsData.map((wordData, index) => (
                <WhWordCard 
                  key={index} 
                  wordData={wordData} 
                  currentLanguage={currentLanguage}
                  speakText={speakText}
                />
              ))}
            </div>
          </TabsContent>

          {/* Flashcards Tab */}
          <TabsContent value="flashcards">
            <VisualFlashcards 
              currentLanguage={currentLanguage}
              speakText={speakText}
            />
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises">
            <TaskBasedExercises 
              currentLanguage={currentLanguage}
              speakText={speakText}
            />
          </TabsContent>

          {/* Quiz Tab */}
          <TabsContent value="quiz">
            <EndQuiz 
              currentLanguage={currentLanguage}
              speakText={speakText}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LessonHub;